import {
  Customer,
  Dimensions,
  MarketplaceOrderApiModel,
  Meta,
  Product,
  ProductApiModel,
  SmartSendItemApiModel
} from '~/lib/api/smartsend/types';
import { CreateMeProductRequest, MeProductApiModel } from '~/lib/api/me/types';
import { isDefault } from '../../my-products/product-helpers';
import { createNewSmartSendItem } from '../panels/createNewSmartSendItem';
import { SumNumber, onlyUniqueNonBlank } from '~/apps/smartsend/lib/helpers/ArrayHelper';
import { ToInlineCommaList } from '~/apps/smartsend/lib/helpers/StringHelper';
import { FindCountryIsoCode, FindCountryName } from '~/apps/smartsend/lib/cache/CountryCache';

export type ActiveSection = 'parcel' | 'recipient' | null;

export const PACKAGE_REQUIRED_VALIDATION_CODES = ['WEIGHT', 'DIMS'];
export const RECIPIENT_REQUIRED_VALIDATION_CODES = ['RECIPIENT-NAME', 'RECIPIENT-TOWN'];

export function createBlankOrderLine(defaultCountryOfManufacture: string): MarketplaceOrderApiModel {
  return {
    MarketplaceOrderId: '',
    Value: 20,
    Quantity: 1,
    Source: { Id: 'manual', Name: 'Manual', AccountId: null },
    Meta: {} as Meta,
    Product: {
      Description: '',
      Sku: '',
      TariffCode: '',
      CountryOfManufacture: FindCountryName(defaultCountryOfManufacture),
      Meta: {}
    } as Product
  } as MarketplaceOrderApiModel;
}

const synthesizeDescription = (lines: MarketplaceOrderApiModel[]): string => {
  const uniqueDescriptions = lines.map((line) => line.Product?.Description).filter(onlyUniqueNonBlank);
  const parts = uniqueDescriptions.map((description) => {
    const quantity = SumNumber(
      lines.filter((line) => line.Product?.Description === description).map((line) => line.Quantity)
    );
    return quantity > 1 || uniqueDescriptions.length > 1 ? `${quantity}x ${description}` : description;
  });
  return ToInlineCommaList(parts);
};

export const lineIsIncomplete = (line: MarketplaceOrderApiModel): boolean =>
  !line.Product?.Description || !line.Value || line.Value <= 0 || (line.Product?.Sku?.length ?? 0) > 50;

// TariffCode/CountryOfManufacture are optional on a My Products product, so they don't factor
// into whether a line is eligible to be saved as one - only Description and Value are required.
export const lineCannotSaveAsProduct = (line: MarketplaceOrderApiModel): boolean =>
  !line.Product?.Description || !line.Value || line.Value <= 0;

export interface AppliedProductSnapshot {
  id: string;
  description: string;
  sku: string;
  value: number;
}

export const snapshotFromLine = (productId: string, line: MarketplaceOrderApiModel): AppliedProductSnapshot => ({
  id: productId,
  description: line.Product?.Description ?? '',
  sku: line.Product?.Sku ?? '',
  value: line.Value
});

// A line only "still matches" the product it was applied from while Description/Sku/Value are
// unchanged - any edit to those fields means it no longer represents that saved product.
export const matchingProductId = (
  snapshot: AppliedProductSnapshot | undefined,
  line: MarketplaceOrderApiModel
): string | null =>
  snapshot &&
  snapshot.description === (line.Product?.Description ?? '') &&
  snapshot.sku === (line.Product?.Sku ?? '') &&
  snapshot.value === line.Value
    ? snapshot.id
    : null;

export const withRecalculatedTotals = (
  item: SmartSendItemApiModel,
  lines: MarketplaceOrderApiModel[]
): SmartSendItemApiModel => ({
  ...item,
  MarketplaceOrderLines: lines,
  Value: SumNumber(lines.map((line) => line.Value)),
  Description: synthesizeDescription(lines)
});

export const applyProductToLine = (
  line: MarketplaceOrderApiModel,
  product: ProductApiModel,
  defaultCountry: string
): MarketplaceOrderApiModel => ({
  ...line,
  Value: product.Value ?? line.Value,
  Product: {
    ...line.Product,
    Description: product.Description ?? line.Product.Description,
    Sku: product.Sku ?? line.Product.Sku,
    TariffCode: product.TariffCode ?? line.Product.TariffCode,
    CountryOfManufacture: FindCountryName(product.CountryOfManufacture || defaultCountry)
  }
});

export const resolveInitialProduct = (
  initialProduct: ProductApiModel | null | undefined,
  products: ProductApiModel[]
): ProductApiModel | null => initialProduct ?? products.find((p) => isDefault(Number(p.Id))) ?? null;

export const buildInitialItem = (
  defaultCountry: string,
  sender: Customer,
  initialProduct: ProductApiModel | null | undefined,
  products: ProductApiModel[]
): SmartSendItemApiModel => {
  const base: SmartSendItemApiModel = {
    ...createNewSmartSendItem(defaultCountry),
    Sender: { ...sender },
    MarketplaceOrderLines: [createBlankOrderLine(defaultCountry)]
  };

  const product = resolveInitialProduct(initialProduct, products);
  if (!product) return base;

  const withProduct = withRecalculatedTotals(base, [
    applyProductToLine(base.MarketplaceOrderLines[0], product, defaultCountry)
  ]);
  if (product.Dimensions) {
    withProduct.PackageDimensions = { ...withProduct.PackageDimensions, ...product.Dimensions };
  }
  return withProduct;
};

// The quick-send saved-products list is now sourced from the Me API (MeProductApiModel) instead
// of the smartsend /product/list endpoint, but every consumer here (applyProductToLine,
// resolveInitialProduct, ProductSelector, etc.) is written against ProductApiModel - map at the
// fetch boundary instead of touching those. CountryOfManufacture is passed through as-is (ISO3);
// applyProductToLine's FindCountryName already resolves an ISO3 code to its canonical Name.
export const mapMeProductToProductApiModel = (p: MeProductApiModel): ProductApiModel => ({
  Id: String(p.id),
  Description: p.contents ?? undefined,
  Sku: p.sku ?? undefined,
  TariffCode: p.tariffCode ?? undefined,
  CountryOfManufacture: p.countryOfManufacture ?? undefined,
  Value: p.estimatedValue ?? undefined,
  Dimensions: {
    Weight: p.weight ?? undefined,
    Height: p.height ?? undefined,
    Width: p.width ?? undefined,
    Length: p.length ?? undefined
  },
  Meta: {}
});

export const buildProductPayloadFromLine = (
  line: MarketplaceOrderApiModel,
  packageDimensions: Dimensions
): CreateMeProductRequest => ({
  contents: line.Product?.Description ?? '',
  sku: line.Product?.Sku ?? null,
  weight: packageDimensions?.Weight ?? null,
  height: packageDimensions?.Height ?? null,
  width: packageDimensions?.Width ?? null,
  length: packageDimensions?.Length ?? null,
  estimatedValue: line.Value ?? null,
  countryOfManufacture: line.Product?.CountryOfManufacture
    ? FindCountryIsoCode(line.Product.CountryOfManufacture) || null
    : null,
  tariffCode: line.Product?.TariffCode ?? null,
  description: null,
  manufacturerProductId: null,
  standardisedProductId: null
});

// Tariff Code and Country of Manufacture are customs fields - only relevant for international
// shipments. Domestic orders should never send them, regardless of what a picked product had.
export const stripCustomsFieldsIfDomestic = (
  lines: MarketplaceOrderApiModel[],
  isInternational: boolean
): MarketplaceOrderApiModel[] =>
  isInternational
    ? lines
    : lines.map((line) => {
        const { TariffCode: _TariffCode, CountryOfManufacture: _CountryOfManufacture, ...rest } = line.Product;
        return { ...line, Product: rest as Product };
      });

export const withoutIndex = <T>(map: Record<number, T>, removedIndex: number): Record<number, T> => {
  const result: Record<number, T> = {};
  Object.entries(map).forEach(([key, value]) => {
    const i = Number(key);
    if (i < removedIndex) result[i] = value;
    if (i > removedIndex) result[i - 1] = value;
  });
  return result;
};
